import { Component, Inject, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DOCUMENT } from '@angular/common';
import { SignalRService } from './core/services/signal-r.service';
import { Router, Event, NavigationStart } from '@angular/router';
import { GlobalService } from './extensions/services/global.service';
import {
  DEFAULT_APP_LANGUAGE,
  DEFAULT_USERNAME,
  USER_NAV_ACTIONS,
  StartupServices,
  RETURN_URL_KEY,
} from './extensions/constants/commonConstants';
import { isNotEmpty } from './extensions/utilities';
import { Observable } from 'rxjs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CookieModalComponent } from './layout/cookie-modal/cookie-modal.component';
import { CookieAPIService } from './extensions/services/api/cookie-api.service';
import { ROUTES } from 'src/app/core/constants/routes-constants';
import { ENV_CONFIG } from './core/injection.token';
import { InactivityService } from './core/services/inactivity.service';
import { LoaderService } from './extensions/services/loader.service';
import { MenuAPIService } from './extensions/services/api/menu-api.service';
import { OAuthService } from 'angular-oauth2-oidc';
import { CssFilePopupComponent } from './extensions/components/css-file-popup/css-file-popup.component';
import { LanguagePopupComponent } from './extensions/components/language-popup/language-popup.component';
import { AuthorizeService } from './core/services/authorize.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit {
  cookieMessage: app.models.CookieModel;

  isAuth: boolean = false;
  isAuthenticated$: Observable<boolean> = new Observable();
  hasUserRights$: Observable<boolean> = new Observable();
  isAdmin: boolean = false;
  username: string = DEFAULT_USERNAME;
  showAppVersion: boolean = true;

  menuItems: app.models.SidebarMenuItems[] = [];

  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject(ENV_CONFIG) private _envConfig: env.EnvConfigurationData,
    private _translateService: TranslateService,
    private _signalRService: SignalRService,
    private _router: Router,
    private _globalService: GlobalService,
    private _oauthService: OAuthService,
    private _authorizeService: AuthorizeService,
    private _cookieAPIService: CookieAPIService,
    private _ngbModalService: NgbModal,
    private _inactivityService: InactivityService,
    private _loaderService: LoaderService,
    private _menuAPIService: MenuAPIService,
    private _ngbModal: NgbModal
  ) {
    this.setCurrentLanguage();
    this.setCurrentPath(this._router);
    this._globalService.initViewModesMap();
    this.isAuthenticated$ = this._authorizeService.isAuthenticated$;
    this.hasUserRights$ = this._globalService.hasUserRights$;
    this._inactivityService.startWatching();
  }

  ngOnInit(): void {
    this._authorizeService.isAuthenticated$.subscribe(isAuth => {
      if (isAuth && this.isAuth !== isAuth) {
        this.isAuth = isAuth;
        this.username = this._globalService.user?.username ?? DEFAULT_USERNAME;
        this.isAdmin = this._globalService.isAdminUser();
        this.showAppVersion = JSON.parse(this._envConfig.SHOW_APP_VERSION || 'true');

        this.setCurrentLanguage();

        this._loaderService.registerService(StartupServices.SidebarMenu);
        this.loadMenuItems();
        this.setCurrentPath(this._router);

        this._signalRService.enableSignalRSubscription();
        const userProfile = this._globalService.user;
        if (userProfile && userProfile.userId && userProfile.isActive) {
          const returnUrl: string | null = localStorage.getItem(RETURN_URL_KEY);
          if (returnUrl) {
            localStorage.removeItem(RETURN_URL_KEY);
            this._router.navigateByUrl(returnUrl);
          }
          this._globalService.hasUserRightsSubject$.next(true);
          this.loadCookieData();
        } else {
          this._globalService.hasUserRightsSubject$.next(false);
          if (isAuth) {
            this._router.navigate(['/' + ROUTES.INVALID_ACCOUNT]);
          }
        }
      }
    });
  }

  loadMenuItems() {
    this._menuAPIService.GetSidebarMenuItems().subscribe(result => {
      this.menuItems = result;
      this._globalService.availableRoutes = result.map(x => x.path).filter(x => x != null);
      this._globalService.obsPathChanged.subscribe(x => {
        let menuItemId: string = '';

        if (!x) {
          menuItemId = this.menuItems.find(item => item.path == ROUTES.DEFAULT_ROUTE_URL).menuItemId;
        } else {
          const xPath = x.includes('?') ? x.substring(0, x.indexOf('?')) : x;
          menuItemId = this.menuItems.find(item =>
            item.path?.includes('/') ? item.path.substring(0, item.path.indexOf('/')) == xPath : item.path == xPath
          )?.menuItemId;
        }

        this.menuItems.forEach(item => {
          item.selected = item.menuItemId == menuItemId;
        });

        this.menuItems = [...this.menuItems];
      });
      this._loaderService.finishServiceLoad(StartupServices.SidebarMenu);
    });
  }

  private setCurrentPath(router: Router) {
    router.events.subscribe((event: Event) => {
      if (event instanceof NavigationStart) {
        var path = event.url.substring(1);
        if (path.includes('/')) {
          this._globalService.pathChanged = path.substring(0, path.indexOf('/'));
        } else {
          this._globalService.pathChanged = path;
        }
      }
    });
  }

  onUserNavOptionSelect(option: string) {
    switch (option) {
      case USER_NAV_ACTIONS.FAQ:
        this._router.navigate(['faq']);
        break;
      case USER_NAV_ACTIONS.MY_PROFILE:
        this._router.navigate(['my-profile']);
        break;
      case USER_NAV_ACTIONS.ABOUT:
        this._router.navigate(['about']);
        break;
      case USER_NAV_ACTIONS.MY_CSS:
        const cssFileModal = this._ngbModal.open(CssFilePopupComponent, { backdrop: 'static' });
        break;
      case USER_NAV_ACTIONS.LANGUAGE_SWITCHER:
        const languageModal = this._ngbModal.open(LanguagePopupComponent, { backdrop: 'static', scrollable: true });
        languageModal.componentInstance.title = 'settings.chooseLanguage';
        languageModal.componentInstance.selectedLanguage = this._globalService.language;
        languageModal.result.then(result => {
          if (result) {
            window.location.reload();
          }
        });
        break;
      case USER_NAV_ACTIONS.LOGOUT:
        this._oauthService.logOut();
        break;
      default:
        break;
    }
  }

  private async setCurrentLanguage() {
    let currentLanguage: string = undefined;
    const urlParams = new URLSearchParams(window.location.search);
    const identityCookieCulture = urlParams.get('culture');
    const localStorageCulture = this._globalService.language;

    if (isNotEmpty(identityCookieCulture)) {
      let identityCookieCultureArray = identityCookieCulture.split('|');
      if (identityCookieCultureArray.length == 3) {
        let cultureTimeStamp = identityCookieCultureArray[2].split('=')[1];
        if (
          !isNotEmpty(this._globalService.languageTimestamp) ||
          this._globalService.languageTimestamp < cultureTimeStamp
        ) {
          currentLanguage = identityCookieCulture.split('|')[1].split('=')[1].slice(0, 2);
          this._globalService.language = currentLanguage;
        }
      }
    }

    if (!isNotEmpty(currentLanguage)) {
      if (isNotEmpty(localStorageCulture)) {
        currentLanguage = localStorageCulture;
      } else if (isNotEmpty(this._envConfig.DEFAULT_APP_LANGUAGE)) {
        currentLanguage = this._envConfig.DEFAULT_APP_LANGUAGE;
        this._globalService.language = currentLanguage;
      } else {
        currentLanguage = DEFAULT_APP_LANGUAGE;
        this._globalService.language = currentLanguage;
      }
    }

    this._translateService.setDefaultLang(currentLanguage);
    this.document.documentElement.lang = currentLanguage;
  }

  openCookieModal(): void {
    const instance = this._ngbModalService.open(CookieModalComponent, {
      modalDialogClass: 'cookie-message-modal',
      backdrop: this.cookieMessage.required ? 'static' : false,
      windowClass: !this.cookieMessage.required ? 'no-blur-window' : '',
      animation: true,
      keyboard: false,
      scrollable: true,
    });

    instance.componentInstance.message = this.cookieMessage.message;
    instance.componentInstance.cookieMessageId = this.cookieMessage.cookieMessageId;

    if (!this.cookieMessage.required) {
      this.document.body.style.overflow = 'auto';
    }
  }

  loadCookieData(): void {
    this._cookieAPIService.getCookieMessage().subscribe(cookieMessage => {
      this.cookieMessage = cookieMessage;
      if (cookieMessage?.message != null) this.openCookieModal();
    });
  }
}
